import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { createContext, useContext } from 'react';

import { mergeClasses } from '@/utils';

import {
  ACCORDION_ITEM_VARIANTS,
  ACCORDION_TRIGGER_VARIANTS,
  type TAccordionVariants,
} from './Accordion.constants';

type TBorderRadius = NonNullable<TAccordionVariants['borderRadius']>;

const AccordionBorderRadiusContext = createContext<TBorderRadius>('sharp');

const Accordion = ({
  className,
  borderRadius = 'sharp',
  ...props
}: AccordionPrimitive.Root.Props & { borderRadius?: TBorderRadius }) => {
  return (
    <AccordionBorderRadiusContext.Provider value={borderRadius}>
      <AccordionPrimitive.Root
        data-slot="accordion"
        className={mergeClasses(
          'flex w-full flex-col bg-neutral-0 dark:bg-neutral-10',
          borderRadius === 'soft' && 'gap-2',
          className,
        )}
        {...props}
      />
    </AccordionBorderRadiusContext.Provider>
  );
};

const AccordionItem = ({ className, ...props }: AccordionPrimitive.Item.Props) => {
  const borderRadius = useContext(AccordionBorderRadiusContext);

  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={mergeClasses(
        'data-disabled:opacity-50',
        ACCORDION_ITEM_VARIANTS({ borderRadius }),
        className,
      )}
      {...props}
    />
  );
};

const AccordionTrigger = ({ className, children, ...props }: AccordionPrimitive.Trigger.Props) => {
  const borderRadius = useContext(AccordionBorderRadiusContext);

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={mergeClasses(
          'group/accordion-trigger relative flex flex-1 items-start justify-between border border-transparent px-4 py-2.5 text-left font-semibold text-body-base text-neutral-9 dark:text-neutral-0 transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:after:border-ring aria-disabled:pointer-events-none aria-disabled:opacity-50 **:data-[slot=accordion-trigger-icon]:ml-auto **:data-[slot=accordion-trigger-icon]:size-4 **:data-[slot=accordion-trigger-icon]:text-muted-foreground',
          ACCORDION_TRIGGER_VARIANTS({ borderRadius }),
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden text-primary-base"
        />
        <ChevronUpIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline text-primary-base"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
};

const AccordionContent = ({ className, children, ...props }: AccordionPrimitive.Panel.Props) => {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      className="overflow-hidden text-body-sm font-medium text-neutral-8 dark:text-neutral-6 data-open:animate-accordion-down data-closed:animate-accordion-up"
      {...props}
    >
      <div
        className={mergeClasses(
          'h-(--accordion-panel-height) px-4 pt-0 pb-2.5 data-ending-style:h-0 data-starting-style:h-0 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
          className,
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Panel>
  );
};

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
